import { DEFAULT_FIRST_SCREEN } from './const/default-first-screen';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, SimpleChange, ViewChild, ViewContainerRef } from '@angular/core';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { LandingTypeEnum, ScreenUsual } from 'src/app/common/onboarding/public-api';
import { DistributionOnboardingService } from 'src/app/common/distribution/onboarding/distribution-onboarding.service';
import { DISTRIBUTION_ONBOARDING_COMPONENTS } from 'src/app/common/distribution/onboarding/distribution-onboarding.types';
import { AnalyticTypesEnum } from 'src/app/common/enums/analytic-types.enum';
import { OnBoarding } from 'src/app/common/models/onboarding';
import { AnalyticService } from 'src/app/common/services/analytic.service';
import { AppendScreenResult, UpdateAnswersKeyMap } from 'src/app/store/actions/user-info.actions';
import { selectAllData } from 'src/app/store/selectors/app.selector';
import { IAppState } from 'src/app/store/states/app.state';
import { HeaderService } from 'src/app/ui/header/header.service';
import { StepService } from 'src/app/ui/screen-step/step.service';

declare const window: any;

@Component({
  selector: 'ik-landing',
  templateUrl: './landing.component.html',
  styleUrls: ['./landing.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LandingComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild('currentContainer', { read: ViewContainerRef })
  public currentContainer!: ViewContainerRef;

  @ViewChild('nextContainer', { read: ViewContainerRef })
  public nextContainer!: ViewContainerRef;

  public allDataObservable = this.store.pipe(select(selectAllData));

  public allData: IAppState | null = null;

  public readonly LANDING_TYPES_ENUM = LandingTypeEnum;

  public onboarding!: OnBoarding;

  public prevScreen!: any | null;

  public currentScreen!: any | null;

  public nextScreen!: any | null;

  public moveNext = true;

  public movePrev = false;

  public index = 0;

  private childAge = '';

  private waiting = false;

  private destroy = new Subject<void>();

  private answersMap = new Map<string, string | string[]>;

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly router: Router,
    private readonly store: Store<IAppState>,
    private readonly analyticService: AnalyticService,
    private readonly onboardingScreenService: DistributionOnboardingService,
    private readonly headerService: HeaderService,
    private readonly stepService: StepService,
  ) {
  }

  public ngOnInit(): void {
    this.checkLastVisit();
    this.subscriptionStore();
    this.analyticService.addUTMParamsIntoCookie();
    this.headerService.changeNeedBurger(true);
  }

  public ngAfterViewInit(): void {
    this.nextScreen = DEFAULT_FIRST_SCREEN;
    this.lazyLoadNextCard();
  }

  public ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  get isStepHidden(): boolean {
    return (
      (this.moveNext && this.nextScreen?.parameters?.stepHidden) ||
      (this.moveNext && this.currentScreen?.parameters?.stepHidden && !this.nextScreen?.parameters?.stepHidden) ||
      (this.movePrev && this.prevScreen?.parameters?.stepHidden) ||
      (this.movePrev && this.currentScreen?.parameters?.stepHidden && !this.prevScreen?.parameters?.stepHidden)
    );
  }

  public next(answer: string): void {
    if (this.waiting || !this.currentScreen) {
      return;
    }
    this.waiting = true;
    setTimeout(() => this.waiting = false, 400);
    const properties: any = {};
    properties.Stage = this.currentScreen.parameters.name;
    properties.onboarding_question = (this.currentScreen.parameters as ScreenUsual).question ? (this.currentScreen.parameters as ScreenUsual).question : '';
    properties.onboarding_answer = answer ? answer : '';
    this.webEndpointByAnswer(properties);
    this.saveResult(answer);
    this.saveUserInfoByAnswer(answer);
    if (this.allData!.userInfo.childAge && this.childAge !== this.allData!.userInfo.childAge) {
      this.childAge = this.allData!.userInfo.childAge;
      // Сбрасываем onboarding до исходного состояния
      this.onboarding = JSON.parse(JSON.stringify(this.allData!.variant.variant!.onboarding));
      this.applyScreenFilters();
      this.setAllSteps();
    } else if (this.answersMap.size > 0) {
      // Если изменились только ответы, применяем фильтры заново
      this.onboarding = JSON.parse(JSON.stringify(this.allData!.variant.variant!.onboarding));
      this.applyScreenFilters();
    }

    this.stepService.next(this.currentScreen);
    if (this.onboarding.screens.length > this.index + 1) {
      this.nextScreen = this.onboarding.screens[this.index + 1].info;
      this.moveNext = true;
      setTimeout(() => this.stopMoveNext(), 400);
    } else {
      this.moveNext = true;
      setTimeout(() => this.goToPayment(), 400);
    }

    this.changeDetectorRef.markForCheck();
  }

  public prev(): void {
    if (this.waiting) {
      return;
    }
    if (this.index) {
      this.prevScreen = this.onboarding.screens[this.index - 1].info;
      this.movePrev = true;
      this.stepService.prev(this.prevScreen);
      setTimeout(() => this.stopMovePrev(), 400);
    }
    this.waiting = true;
    setTimeout(() => this.waiting = false, 400);
    this.changeDetectorRef.markForCheck();
  }

  private saveResult(answer: string | undefined): void {
    if (!answer) return; // Skip if answer is undefined
    const question = this.currentScreen.parameters.question || this.currentScreen.parameters.title;
    const result = {
        screen: {
            custom_id: this.currentScreen.parameters.name, // "NewOB_08_24_002"
            id: this.currentScreen.id, // "20f36e1c-cdc0-462a-81a0-c87d3246cca3"
            index: this.index // 22 in the example
        },
        element: {
            custom_id: question, // "choose_gender"
            id: question, // "choose_gender"
            type: "Options"
        },
        state: {
            value: answer // e.g., "boy" or "girl" based on the subjects
        }
    };

    this.store.dispatch(new AppendScreenResult(result));
}

  private applyScreenFilters() {
    this.onboarding.screens = this.onboarding.screens.filter(item => {
      // Фильтр по возрасту
      const ageFilterPass = !item.info.parameters.ageFilter ||
                           !item.info.parameters.ageFilter.length ||
                           item.info.parameters.ageFilter.includes(this.childAge);
  
      // Фильтр по ответам
      const commonFilterPass = !item.info.parameters.commonFilter ||
                              !item.info.parameters.commonFilter.length ||
                              item.info.parameters.commonFilter.some((elem: {key: string, value: string}) => {
                                return this.answersMap.get(elem.key) === elem.value;
                              });
  
      // Экран проходит, если выполняются оба условия
      return ageFilterPass && commonFilterPass;
    });
  }

  private checkLastVisit(): void {
    const lastDate = this.getCookie('lastDate');
    const oneDay = 24 * 60 * 60 * 1000;
    const now = new Date();
    if (!!lastDate && now.getTime() - new Date(lastDate).getTime() < oneDay) {
      this.goToPayment();
    }
  }

  private getCookie(name: string): string {
    const matches = document.cookie.match(new RegExp(`(?:^|; )${ name.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, '\\$1') }=([^;]*)`));
    return matches ? decodeURIComponent(matches[1]) : '';
  }

  private goToPayment(): void {
    const pathname = window.location.pathname;
    this.router.navigate([`${ pathname }/pay`], { queryParamsHandling: 'merge' });
  }

  private webEndpointByAnswer(properties: any = {}): void {
    this.analyticService
      .analyticWebPoint(AnalyticTypesEnum.WebOnboardingStageCompleted, properties)
      .subscribe();
  }

  private saveUserInfoByAnswer(result: string): void {
    if (this.currentScreen.parameters.saveAnswer) {
      
      const key = this.currentScreen.parameters.saveAs;
      let arr: string[] = [];
      switch (this.currentScreen.type) {
        case LandingTypeEnum.TWO_BUTTONS: {
          arr = ['Answer_no', 'Answer_yes'];
          break;
        }
        case LandingTypeEnum.QUESTION: {
          arr = ['Answer_yes', 'Answer_no', 'Answer_maybe'];
          break;
        }
        default: {
          arr = this.currentScreen.parameters.subjects;
        }
      }
      const index = arr.findIndex(item => item === result);
      let answer: string | string[] = '';
      if (index === -1) {
        if (result.indexOf(',') !== -1) {
          const results = result.split(',');
          answer = results.map(elem => {
            const index2 = arr.findIndex(item => item === elem);

            return index2 <= this.currentScreen.parameters.answerKeys.length - 1 ? this.currentScreen.parameters.answerKeys[index2] : '';
          });
        }
      } else {
        answer = index <= this.currentScreen.parameters.answerKeys.length - 1 ? this.currentScreen.parameters.answerKeys[index] : '';
      }
      this.store.dispatch(new UpdateAnswersKeyMap({ key, answer }));
    }
  }

  private stopMoveNext(): void {
    this.moveNext = false;
    this.setScreen(this.nextScreen);
    if (this.onboarding.screens.length > this.index + 2) {
      this.nextScreen = this.onboarding.screens[this.index + 2].info;
    } else {
      this.nextScreen = null;
    }
    this.index ++;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (this.index === 1) {
      this.headerService.changeNeedBurger(false);
    }

    this.lazyLoadCurrentCard();
    this.changeDetectorRef.markForCheck();
  }

  private stopMovePrev(): void {
    this.movePrev = false;
    this.setScreen(this.prevScreen);
    this.prevScreen = null;
    this.index --;
    if (this.index === 0) {
      this.headerService.changeGoBackBtnVisible(false);
      this.headerService.changeNeedBurger(true);
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.lazyLoadCurrentCard();
    this.changeDetectorRef.markForCheck();

  }

  private setFirstScreen(): void {
    if (!this.onboarding.screens || !this.onboarding.screens.length) {
      this.goToPayment();
      return;
    }
    this.onboarding.screens = this.onboarding.screens.sort((a, b) => a.order - b.order);
    this.setScreen(this.onboarding.screens[0].info);
    this.headerService.changeGoBackBtnVisible(false);
    this.headerService.changeNeedBurger(true);
    this.lazyLoadCurrentCard();
    setTimeout(() => {
      this.moveNext = false;
      this.changeDetectorRef.markForCheck();
    },         200);
  }

  private setScreen(screen: any): void {
    this.currentScreen = screen;
    this.headerService.setStateByScreen(this.currentScreen.type, this.currentScreen);
    this.stepService.setSettingsForScreen(this.currentScreen);
  }

  private setAllSteps(): void {
    this.stepService.setAllStepsFromOnboarding(this.onboarding.screens);
    this.stepService.setSectionsFromVariant(this.allData!.variant.variant!, this.onboarding.screens);
  }

  private subscriptionStore(): void {
    this.allDataObservable
    .pipe(
      takeUntil(this.destroy),
      filter(data => !!data),
      tap(data => {
        this.answersMap = data.userInfo.answersMap
      }),
      filter(data => !!data && !!data.variant && !!data.variant.variant),
    )
    .subscribe(data => {
      if (data!.userInfo.childAge && this.childAge !== data!.userInfo.childAge) {
        this.allData = data;
      }
      if (!this.onboarding) {
        this.allData = data;
        this.onboarding = JSON.parse(JSON.stringify(data!.variant.variant!.onboarding));
        this.setFirstScreen();
        this.setAllSteps();
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  private async lazyLoadCurrentCard(): Promise<void> {
    if (!this.currentScreen) return;
    const quizCardFactory = await this.onboardingScreenService.getComponentFactory(this.currentScreen);
    const newComponent = this.currentContainer.createComponent<DISTRIBUTION_ONBOARDING_COMPONENTS>(quizCardFactory);
    if (this.currentContainer) {
      this.currentContainer.detach(0);
      this.currentContainer.insert(newComponent.hostView, 0);
    }
    newComponent.instance.screen = this.currentScreen.parameters;
    if (newComponent.instance.current !== undefined) {
      newComponent.instance.current = true;
    }
    newComponent.instance.goNext.subscribe((data: string) => {
      this.next(data);
    });
    (newComponent.instance as any).ngOnChanges([new SimpleChange(null, this.currentScreen.parameters, true)]);
    if (this.onboarding.screens.length > this.index + 1) {
      this.nextScreen = this.onboarding.screens[this.index + 1].info;
      this.lazyLoadNextCard();
    }
  }

  private async lazyLoadNextCard(): Promise<void> {
    if (!this.nextScreen) return;
    const quizCardFactory = await this.onboardingScreenService.getComponentFactory(this.nextScreen);
    const nextComponent = this.nextContainer.createComponent<DISTRIBUTION_ONBOARDING_COMPONENTS>(quizCardFactory);
    if (this.nextContainer) {
      this.nextContainer.detach(0);
      this.nextContainer.insert(nextComponent.hostView, 0);
    }
    nextComponent.instance.screen = this.nextScreen.parameters;
    (nextComponent.instance as any).ngOnChanges([new SimpleChange(null, this.nextScreen.parameters, true)]);
  }
}
